import React from 'react';
import { M } from '@dashboard-experience/mastodon';
import {
  AssessStatuses,
  ReportStatuses,
  StatusTypes,
} from '@dashboard-experience/utils';
import { i18n as mastodonI18n } from '@international/mastodon-i18n';
import styled from 'styled-components';
import { StyledAssessStatusBadge } from './StatusBadge';
import StatusBadgeAssessComplete from './StatusBadgeAssessComplete';

interface StatusBadgeWithInfoProps {
  status: string;
  statusType: string;
}

const assessBadgeStyle = {
  padding: '2px 8px 2px 22px',
  borderRadius: '.625rem',
  fontSize: '12px',
  height: '1.25rem',
  lineHeight: 'calc(1.25rem - 4px)',
  color: '#1a2026',
  border: '0',
  display: 'inline-block',
  fontWeight: 500,
};

const statusesWithoutInfo = ['submitted', AssessStatuses.ELIGIBLE];

const InfoBadgeStyled = styled.span`
  display: inline-flex;

  .cds--toggletip-button,
  .status-badge-trigger {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 0;
    border: 0;
    background: none;
    cursor: pointer;
  }

  .cds--toggletip-button:focus,
  .cds--tooltip-trigger__wrapper button:focus {
    outline: none !important;
    box-shadow: none !important;
  }

  .cds--toggletip-button:focus-visible .status-badge-trigger,
  .cds--tooltip-trigger__wrapper button:focus-visible .status-badge-trigger {
    outline: 2px solid #0f62fe;
    outline-offset: 2px;
  }

  .status-badge-info svg {
    width: 16px;
    height: 16px;
    margin: 0 !important;
    fill: #1a2026;
  }
`;

const StatusBadgeWithInfo: React.FC<StatusBadgeWithInfoProps> = ({
  status,
  statusType,
}) => {
  const label = mastodonI18n.getStr(`badges.${status}`);
  const badgeProps = {
    'data-testid': 'status-badge',
    className: 'text-capitalize',
    statusType,
    label,
    status: status === 'submitted' ? ReportStatuses.COMPLETE : status,
  };

  let badge = <M.StatusBadge {...badgeProps} />;
  if (statusType === StatusTypes.Assess) {
    badge = (
      <StyledAssessStatusBadge
        as='span'
        data-testid='styled-assess-status-badge'
      >
        {badgeProps.status === ReportStatuses.COMPLETE ? (
          <StatusBadgeAssessComplete
            {...badgeProps}
            badgeStyle={assessBadgeStyle}
          />
        ) : (
          <M.StatusBadge {...badgeProps} style={assessBadgeStyle} />
        )}
      </StyledAssessStatusBadge>
    );
  }

  if (statusesWithoutInfo.includes(status)) {
    return badge;
  }

  const explanation = mastodonI18n.getStr(
    `components.Report.BadgeStatusExplanation.${status}`,
  );
  const trigger = (
    <span className='status-badge-trigger'>
      {badge}
      <span className='status-badge-info'>
        <M.Icon icon='InformationFilled' />
      </span>
    </span>
  );
  const canHover = globalThis.matchMedia?.('(hover: hover)').matches;

  return (
    <InfoBadgeStyled>
      {canHover ? (
        <M.Tooltip align='left' highlighted={false} label={explanation}>
          {trigger}
        </M.Tooltip>
      ) : (
        <M.Toggletip align='left'>
          <M.ToggletipButton
            label={mastodonI18n.getStr(
              'components.Report.BadgeStatusInfoLabel',
              label,
            )}
          >
            {trigger}
          </M.ToggletipButton>
          <M.ToggletipContent>
            <p>{explanation}</p>
          </M.ToggletipContent>
        </M.Toggletip>
      )}
    </InfoBadgeStyled>
  );
};

export default StatusBadgeWithInfo;
